import { useEffect } from 'react';
import { GAP_NOTE_DISMISS_MS, type GapNote } from '../lib/synergyR11';
import { Icon } from './Icon';

/**
 * Knowledge-gap "noted" toast (R11 #6 — showcase-conversation-synergy.md §6 #6, client-scenarios.md
 * S1 beat 8 / S2 beat 3). When Gigi escalates a question she can't answer ("that's one for the
 * team"), this quiet overlay chip appears on the showcase: "Noted — the team will include this in
 * your follow-up ✓" (+ the topic).
 *
 * Design intent — escalation must read as SERVICE, not failure:
 *   - NO error styling (no terracotta/danger, no alert icon) — a calm surface + brass check.
 *   - An overlay CHIP, not a scene change — it never interrupts the current scene (browse /
 *     order-review keep the stage; this floats over the bottom of the showcase panel).
 *   - Auto-dismisses after GAP_NOTE_DISMISS_MS; also dismissable by tap (touch is a full peer).
 *
 * Tokens only, day+night aware, `.reveal` fade+rise (reduced-motion safe via the motion tokens).
 */
export function GapNoteToast({ note, onDismiss }: { note: GapNote; onDismiss: () => void }) {
  // Auto-dismiss. Re-armed whenever a new note arrives (topic change → fresh window).
  useEffect(() => {
    const t = setTimeout(onDismiss, GAP_NOTE_DISMISS_MS);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-[var(--z-overlay)] flex justify-center px-4 pb-4"
      // aria-live polite: announced calmly, not assertively (this is reassurance, not an error).
      aria-live="polite"
    >
      <button
        type="button"
        data-testid="gap-note-toast"
        onClick={onDismiss}
        className="reveal pointer-events-auto flex max-w-md items-start gap-3 rounded-xl bg-surface/95 px-4 py-3 text-left ring-1 ring-line shadow-elev-lift backdrop-blur-sm"
      >
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brass-soft ring-1 ring-brass/30">
          <Icon name="check" size={13} className="text-brass" />
        </span>
        <span className="flex flex-col gap-0.5">
          <span className="text-sm text-ink leading-snug">
            Noted — the team will include this in your follow-up
          </span>
          {note.topic !== '' && (
            <span className="text-[11px] text-subtle leading-snug">{note.topic}</span>
          )}
        </span>
        <span aria-hidden className="mt-0.5 shrink-0 text-faint">
          <Icon name="x" size={13} />
        </span>
      </button>
    </div>
  );
}
